import { Field, FieldInput, FieldLabel, FieldSelect } from '@ibe/ui';
import { Controller, type Control, type FieldErrors, type UseFormRegister } from 'react-hook-form';

import { type TenantFormInput } from '../api/schemas.js';

const AWS_REGION_OPTIONS = [
  { value: 'us-east-1', label: 'us-east-1' },
  { value: 'us-east-2', label: 'us-east-2' },
  { value: 'us-west-1', label: 'us-west-1' },
  { value: 'us-west-2', label: 'us-west-2' },
  { value: 'af-south-1', label: 'af-south-1' },
  { value: 'ap-east-1', label: 'ap-east-1' },
  { value: 'ap-south-1', label: 'ap-south-1' },
  { value: 'ap-south-2', label: 'ap-south-2' },
  { value: 'ap-southeast-1', label: 'ap-southeast-1' },
  { value: 'ap-southeast-2', label: 'ap-southeast-2' },
  { value: 'ap-southeast-3', label: 'ap-southeast-3' },
  { value: 'ap-southeast-4', label: 'ap-southeast-4' },
  { value: 'ap-northeast-1', label: 'ap-northeast-1' },
  { value: 'ap-northeast-2', label: 'ap-northeast-2' },
  { value: 'ap-northeast-3', label: 'ap-northeast-3' },
  { value: 'ca-central-1', label: 'ca-central-1' },
  { value: 'ca-west-1', label: 'ca-west-1' },
  { value: 'eu-central-1', label: 'eu-central-1' },
  { value: 'eu-central-2', label: 'eu-central-2' },
  { value: 'eu-west-1', label: 'eu-west-1' },
  { value: 'eu-west-2', label: 'eu-west-2' },
  { value: 'eu-west-3', label: 'eu-west-3' },
  { value: 'eu-south-1', label: 'eu-south-1' },
  { value: 'eu-south-2', label: 'eu-south-2' },
  { value: 'eu-north-1', label: 'eu-north-1' },
  { value: 'il-central-1', label: 'il-central-1' },
  { value: 'me-south-1', label: 'me-south-1' },
  { value: 'me-central-1', label: 'me-central-1' },
  { value: 'sa-east-1', label: 'sa-east-1' },
] as const;

export function TenantAttachmentCredentials({
  register,
  errors,
  control,
}: {
  readonly register: UseFormRegister<TenantFormInput>;
  readonly errors: FieldErrors<TenantFormInput>;
  readonly control: Control<TenantFormInput>;
}): React.JSX.Element {
  const credentialErrors = errors.attachmentCredentials;
  return (
    <section className="space-y-4">
      <div>
        <h2 className="text-base font-semibold">Tenant S3 storage</h2>
        <p className="text-muted-foreground text-sm">
          Required for tenant attachments. Values are encrypted before storage and are never written
          to the audit log.
        </p>
      </div>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Field error={credentialErrors?.AWS_REGION?.message} required>
          <FieldLabel>AWS region</FieldLabel>
          <Controller
            control={control}
            name="attachmentCredentials.AWS_REGION"
            render={({ field }) => (
              <FieldSelect
                value={field.value ?? ''}
                onValueChange={field.onChange}
                onBlur={field.onBlur}
                options={AWS_REGION_OPTIONS}
                placeholder="Select an AWS region"
                searchable
              />
            )}
          />
        </Field>
        <Field error={credentialErrors?.AWS_S3_BUCKET?.message} required>
          <FieldLabel>S3 bucket</FieldLabel>
          <FieldInput autoComplete="off" {...register('attachmentCredentials.AWS_S3_BUCKET')} />
        </Field>
        <Field error={credentialErrors?.AWS_ACCESS_KEY_ID?.message} required>
          <FieldLabel>AWS access key ID</FieldLabel>
          <FieldInput autoComplete="off" {...register('attachmentCredentials.AWS_ACCESS_KEY_ID')} />
        </Field>
        <Field error={credentialErrors?.AWS_SECRET_ACCESS_KEY?.message} required>
          <FieldLabel>AWS secret access key</FieldLabel>
          <FieldInput
            type="password"
            autoComplete="new-password"
            {...register('attachmentCredentials.AWS_SECRET_ACCESS_KEY')}
          />
        </Field>
      </div>
    </section>
  );
}
